// FILE: src/pages/admin/student/AdminStudentOverviewPage.jsx
// Overview of one account (WP-7, B-21; MTO 11.1): the figures at a glance,
// identity and registration (registration number, licence), the one-year
// enrolment (MTO 6.3) and the account's sign-in state.
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Alert, Button, Stack } from "@mui/material";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import { DetailList, Section, StatCard } from "../../../components/ui";
import { FigureGroup } from "../../../components/admin/AdminBits";
import { formatDate, formatDateTime, formatNumber } from "../../../components/admin/format";
import { formatDuration } from "../../../utils/timeFormat";
import { licenceClassLabel, roleLabel } from "../../../i18n/labels";
import { useStudent } from "./AdminStudentLayout";
import { lockReasonText } from "../../../components/admin/studentStatus";
import usePermissions from "../../../hooks/usePermissions";

/** "120 days left" / "Ended 3 Oct 2026" / "Starts when they register". */
export function enrolmentSummary(student) {
  const e = student?.enrolment || {};
  if (student?.invited || (!e.startedAt && !student?.enrolmentStartedAt)) {
    return { value: "Not started", note: "Starts when they finish registering" };
  }
  if (e.ended) {
    return { value: "Ended", note: e.expiresAt ? `Ended ${formatDate(e.expiredAt || e.expiresAt)}` : "The one-year enrolment is over" };
  }
  const days = Number(e.daysLeft);
  return {
    value: Number.isFinite(days) ? `${formatNumber(days)} ${days === 1 ? "day" : "days"} left` : "—",
    note: e.expiresAt ? `Ends ${formatDate(e.expiresAt)}` : undefined
  };
}

function licence(student) {
  if (!student.licenseClass && !student.licenseNumber) return "Not given";
  return [student.licenseClass ? licenceClassLabel(student.licenseClass) : null, student.licenseNumber].filter(Boolean).join(" · ");
}

export default function AdminStudentOverviewPage() {
  const { student } = useStudent();
  const { can } = usePermissions();
  const staff = student.role === "admin";
  const p = student.progressSummary || {};
  const minutes = p.creditedMinutes || {};
  const chapters = p.chapters || {};
  const homelinks = p.homelinks || {};
  const tickets = student.supportTickets || {};
  const enrolment = enrolmentSummary(student);
  const e = student.enrolment || {};
  const base = `/admin/users/${student._id}`;

  return (
    <Stack spacing={2.5}>
      {!staff && (
        <FigureGroup id="glance" title="At a glance" columns={4}>
          <StatCard
            icon={MenuBookOutlinedIcon}
            label="Chapters completed"
            value={`${formatNumber(chapters.completed ?? 0)} of ${formatNumber(chapters.total ?? 0)}`}
            progress={chapters.total ? (chapters.completed / chapters.total) * 100 : 0}
            progressLabel="Chapters completed"
            note={`${formatNumber(homelinks.completed ?? 0)} of ${formatNumber(homelinks.total ?? 0)} homelinks`}
            testId="overview-chapters"
          />
          <StatCard
            icon={ScheduleOutlinedIcon}
            label="Time credited"
            value={formatDuration((Number(minutes.total) || 0) * 60)}
            note={`${formatDuration((Number(minutes.core) || 0) * 60)} core · ${formatDuration((Number(minutes.flex) || 0) * 60)} flex`}
            testId="overview-time"
          />
          <StatCard
            icon={EventOutlinedIcon}
            label="Enrolment"
            value={enrolment.value}
            note={enrolment.note}
            tone={e.ended ? "warning" : Number(e.daysLeft) <= 30 && Number.isFinite(Number(e.daysLeft)) ? "warning" : "primary"}
            testId="overview-enrolment"
          />
          <StatCard
            icon={SupportAgentOutlinedIcon}
            label="Support requests"
            value={`${formatNumber(tickets.open ?? 0)} open`}
            note={`${formatNumber(tickets.total ?? 0)} in total`}
            testId="overview-support"
          >
            {can("support.view") && (
              <Button component={RouterLink} to={`${base}/support`} sx={{ mt: 1, px: 0 }}>
                See requests and survey
              </Button>
            )}
          </StatCard>
        </FigureGroup>
      )}

      {student.flags?.untimed && (
        <Alert severity="warning">
          {student.untimedHonoured
            ? "This is an untimed (reviewer) account: its time isn't limited and it isn't part of the compliance figures."
            : "This account is marked untimed, but untimed accounts aren't allowed on this site, so the normal time rules apply."}
        </Alert>
      )}

      <Section id="identity" title="Identity and registration">
        <DetailList
          items={[
            { label: "Full name", value: [student.firstName, student.middleName, student.lastName].filter(Boolean).join(" ") || "Not given" },
            { label: "Email", value: student.email },
            { label: "Phone", value: student.phone || "Not given" },
            { label: staff ? "Account" : "Role", value: roleLabel(student.role) },
            staff && { label: "Staff role", value: student.staffRole?.name || "No role: ask an administrator to choose one" },
            !staff && { label: "Registration number", value: student.registrationNumber || "Not assigned yet" },
            !staff && { label: "Licence", value: licence(student) },
            staff && { label: "DDI registration number", value: student.staffRegistration || "None: can't answer course-content questions" },
            { label: "Added on", value: formatDate(student.registrationDate || student.createdAt) }
          ]}
        />
      </Section>

      {!staff && (
        <Section id="enrolment" title="Enrolment" description="Students have one year from registration to finish the course.">
          <DetailList
            items={[
              { label: "Status", value: student.invited ? "Invitation sent, not registered yet" : e.ended ? "Enrolment ended" : "Enrolled" },
              { label: "Started", value: e.startedAt ? formatDate(e.startedAt) : "Not started" },
              { label: "Ends", value: e.expiresAt ? formatDate(e.expiresAt) : "—" },
              { label: "Days left", value: e.ended ? "None" : e.daysLeft != null ? formatNumber(e.daysLeft) : "—" },
              e.expiredAt && { label: "Ended on", value: formatDate(e.expiredAt) },
              { label: "Course finished", value: p.courseComplete ? formatDate(p.courseCompletedAt) : "Not yet" },
              { label: "Course survey", value: student.survey?.submitted ? `Sent ${formatDate(student.survey.submittedAt)}` : "Not sent yet" }
            ]}
          />
        </Section>
      )}

      <Section id="account" title="Account and sign-in">
        <DetailList
          items={[
            { label: "Account", value: student.accountLocked ? "Locked" : "Active" },
            student.accountLocked && { label: "Locked since", value: formatDateTime(student.lockedAt) },
            student.accountLocked && student.lockReason && { label: "Lock reason", value: lockReasonText(student.lockReason) },
            { label: "Failed sign-in attempts", value: formatNumber(student.failedAttempts ?? 0) },
            { label: "Authenticator app", value: student.twoFactorEnabled ? "On" : "Off" },
            !staff && {
              label: "Security questions",
              value: `${formatNumber(student.challengeQuestionCount ?? 0)} of 5 saved`
            },
            !staff && { label: "Consent", value: student.hasConsent ? "Given" : "Not given yet" },
            { label: "Last active", value: student.lastActive ? formatDateTime(student.lastActive) : "Never" }
          ]}
        />
        {can("audit.view") && (
          <Button component={RouterLink} to={`${base}/activity`} variant="outlined" sx={{ mt: 2.5 }}>
            See sign-ins and other activity
          </Button>
        )}
      </Section>
    </Stack>
  );
}
